import type { VoiceStreamEvent } from "@rakazo/contracts";
import { VoiceStreamEventSchema } from "@rakazo/contracts";

/** Versioned transport shared by browser hosts; provider details never reach clients. */
export function connectVoiceStream(input: {
  url: string;
  ticket: string;
  signal: AbortSignal;
  onEvent: (event: VoiceStreamEvent) => void;
  socketFactory?: (url: string) => WebSocket;
}) {
  const socket = input.socketFactory?.(input.url) ?? new WebSocket(input.url);
  let closed = false,
    ready = false,
    finished = false;
  const timeout = setTimeout(() => fail("语音连接超时"), 20000);
  const close = () => {
    if (closed) return;
    closed = true;
    clearTimeout(timeout);
    input.signal.removeEventListener("abort", close);
    socket.close();
  };
  const fail = (message: string) => {
    if (closed) return;
    input.onEvent({ type: "error", message });
    close();
  };
  input.signal.addEventListener("abort", close, { once: true });
  if (input.signal.aborted) close();
  socket.onopen = () => {
    if (!closed)
      socket.send(JSON.stringify({ type: "authenticate", version: 1, ticket: input.ticket }));
  };
  socket.onerror = () => fail("无法连接语音服务");
  socket.onclose = () => {
    if (!closed && !finished) fail("语音连接已断开");
  };
  socket.onmessage = (event) => {
    if (closed) return;
    try {
      const data = VoiceStreamEventSchema.parse(JSON.parse(String(event.data)));
      if (data.type === "ready") {
        ready = true;
        clearTimeout(timeout);
      }
      if (data.type === "done") finished = true;
      input.onEvent(data);
      if (data.type === "error" || data.type === "done") close();
    } catch {
      fail("语音响应无效");
    }
  };
  const appendBase64 = (audio: string, captureSeq?: number) => {
    if (!ready || closed) return;
    if (socket.bufferedAmount > 1024 * 1024 || audio.length > 44000) {
      fail("语音网络连接过慢");
      return;
    }
    socket.send(
      JSON.stringify({ type: "audio", audio, ...(captureSeq === undefined ? {} : { captureSeq }) }),
    );
  };
  return {
    close,
    playbackDone(responseId: string) {
      if (ready && !closed) socket.send(JSON.stringify({ type: "playbackDone", responseId }));
    },
    appendBase64,
    append(audio: Uint8Array) {
      if (!ready || closed) return;
      if (socket.bufferedAmount > 1024 * 1024) {
        fail("语音网络连接过慢");
        return;
      }
      let binary = "";
      for (const byte of audio) binary += String.fromCharCode(byte);
      appendBase64(btoa(binary));
    },
    finishInput() {
      if (!ready || closed) return;
      socket.send(JSON.stringify({ type: "finishInput" }));
    },
  };
}
